import { useEffect, useMemo, useRef, useState } from 'react';
import { Bug, Database, Languages, Lock, Percent, ReceiptText } from 'lucide-react';

import { AdBanner } from './components/AdBanner';
import { BatchProgress } from './components/BatchProgress';
import { BatchSummary } from './components/BatchSummary';
import { BrandMark } from './components/BrandMark';
import { CookieBanner } from './components/CookieBanner';
import { CropModal } from './components/CropModal';
import { DebugFolderControl } from './components/DebugFolderControl';
import { DonationDialog } from './components/DonationDialog';
import { Dropzone } from './components/Dropzone';
import { ExportMenu, type ExportFormat } from './components/ExportMenu';
import { MarketingContent } from './components/MarketingContent';
import { MarketingHero } from './components/MarketingHero';
import { ReceiptLanguageDialog } from './components/ReceiptLanguageDialog';
import { ScanAnother } from './components/ScanAnother';
import { SessionsList } from './components/SessionsList';
import { ThemeToggle } from './components/ThemeToggle';
import { useReceiptProcessor } from './hooks/useReceiptProcessor';
import { trackEvent } from './lib/analytics';
import { rememberVendorCategory } from './lib/categories';
import { clearConsent, loadConsent } from './lib/consent';
import { DEBUG } from './lib/debug';
import { exportBillCsvBatch } from './lib/export-bill-csv';
import { exportCsvBatch } from './lib/export-csv';
import { exportXlsxBatch } from './lib/export-xlsx';
import { exportZipBatch } from './lib/export-zip';
import { triggerDownload } from './lib/download';
import { batchFilename } from './lib/filename';
import { t, type UiLangCode } from './lib/i18n';
import { buildSampleReceiptFile } from './lib/sample-receipt';
import { loadAllSessions } from './lib/storage';
import { expandZips } from './lib/unzip';
import { useAppStore } from './store';
import { type CategoryCode, LANG_OPTIONS, LANG_META } from './lib/types';

// 3-letter Tesseract code → 2-letter `<html lang>` value. Read from
// the single LANG_META map in types.ts so the build-time generators
// (gen-language-pages, gen-static-pages, gen-guides) and the runtime
// app share one source of truth.
const HTML_LANG: Record<UiLangCode, string> = {
  eng: LANG_META.eng!.lang2,
  spa: LANG_META.spa!.lang2,
  fra: LANG_META.fra!.lang2,
  deu: LANG_META.deu!.lang2,
  nld: LANG_META.nld!.lang2,
  ita: LANG_META.ita!.lang2,
  por: LANG_META.por!.lang2,
};

// Prefix the path for static info pages (about / faq / contact / terms)
// with the active language so /es/, /fr/, etc. visitors stay in their
// locale when they click footer links. English has no prefix and uses
// the root-level page. Privacy is English-only — never prefixed.
function staticPagePath(lang: UiLangCode, page: 'about' | 'faq' | 'contact' | 'terms' | 'privacy'): string {
  // Clean URLs — Apache's .htaccess maps /foo ↔ /foo.html so the
  // browser address bar never shows the extension.
  if (page === 'privacy') return '/privacy';
  const lang2 = HTML_LANG[lang] ?? 'en';
  return lang2 === 'en' ? `/${page}` : `/${lang2}/${page}`;
}

export function App() {
  const {
    processFiles,
    cancel,
    setDebugFolder,
    reprocessWithManualQuad,
    confirmScan,
    reprocessWithRotation,
    reprocessWithLang,
  } = useReceiptProcessor();
  const sessions = useAppStore((s) => s.sessions);
  const updateReceipt = useAppStore((s) => s.updateReceipt);
  const removeSession = useAppStore((s) => s.removeSession);
  const removeTerminal = useAppStore((s) => s.removeTerminal);
  const reorderSessions = useAppStore((s) => s.reorderSessions);
  const lang = useAppStore((s) => s.lang);
  const setLang = useAppStore((s) => s.setLang);
  const ocrLang = useAppStore((s) => s.ocrLang);
  const setOcrLang = useAppStore((s) => s.setOcrLang);
  const showDebug = useAppStore((s) => s.showDebug);
  const setShowDebug = useAppStore((s) => s.setShowDebug);
  const persistEnabled = useAppStore((s) => s.persistEnabled);
  const setPersistEnabled = useAppStore((s) => s.setPersistEnabled);
  const excludeTaxFromExports = useAppStore((s) => s.excludeTaxFromExports);
  const setExcludeTaxFromExports = useAppStore((s) => s.setExcludeTaxFromExports);
  const hydrate = useAppStore((s) => s.hydrate);

  // The CropModal is controlled by the id of the session being re-cropped.
  // null means no modal open; we look up the session from the sessions list
  // every render (cheap — list is small) so the modal automatically gets
  // the latest originalFile reference.
  const [croppingId, setCroppingId] = useState<string | null>(null);
  // Files held back by the first-upload language prompt. Non-null means
  // ReceiptLanguageDialog is showing; they dispatch once it resolves.
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  // Pending export: when set, a DonationDialog is showing and a download
  // of this format will fire when the modal's countdown ends or the user
  // skips. Cleared back to null after the download triggers.
  const [pendingExport, setPendingExport] = useState<ExportFormat | null>(null);
  // Cookie consent: render the banner only when the user hasn't
  // chosen yet. Set once at mount via lazy initialiser; flipping back
  // to true (when the footer link clears consent) is handled by the
  // reload that follows clearConsent so the banner naturally
  // re-mounts on the fresh page.
  const [showConsent, setShowConsent] = useState<boolean>(() => loadConsent() === null);
  const croppingSession = useMemo(
    () =>
      croppingId === null
        ? null
        : sessions.find((s) => s.id === croppingId) ?? null,
    [croppingId, sessions],
  );

  // Keep the document's lang attribute in sync so screen readers
  // pronounce content with the right language profile and the browser
  // applies per-language hyphenation. The static value in index.html
  // is just the boot default; this effect takes over once React has
  // rendered.
  useEffect(() => {
    document.documentElement.lang = HTML_LANG[lang] ?? 'en';
  }, [lang]);

  // First-mount hydrate from IndexedDB when persistence is enabled.
  // Runs ONCE per page load — `hydratedRef` guards against StrictMode
  // double-invoke. After the initial restore the store is the source
  // of truth and IDB only sees write-throughs from terminal mutations.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    if (!persistEnabled) return;
    void loadAllSessions().then((restored) => {
      if (restored.length > 0) hydrate(restored);
    });
  }, [persistEnabled, hydrate]);

  // Batch export across every completed session in one combined file.
  const doneSessions = sessions.filter(
    (s) => s.state.kind === 'success' && s.receipt !== null,
  );
  const doneCount = doneSessions.length;

  // Run dropped files through the ZIP-expansion pass before dispatching
  // them to the worker pool. Most files pass through unchanged; a ZIP
  // gets unpacked into its inner images / PDFs (lazy-loads jszip on
  // first ZIP only). Used by the dropzone, the mobile scan-another
  // button, and the sample-receipt onboarding.
  const handleFiles = async (files: File[]): Promise<void> => {
    const expanded = await expandZips(files);
    if (expanded.length === 0) return;
    // First upload ever: we don't know what language the receipts are in
    // and won't guess. Park the files and let ReceiptLanguageDialog
    // answer that, then dispatch from its onConfirm.
    if (ocrLang === null) {
      // Append rather than replace — a second drop / paste while the
      // dialog is open must not silently discard the first batch.
      setPendingFiles((prev) => [...(prev ?? []), ...expanded]);
      return;
    }
    processFiles(expanded, ocrLang);
    trackEvent('file_uploaded', { count: expanded.length });
  };

  // OCR language for an EXISTING session. Prefers what the receipt was
  // actually scanned with so a re-crop / rotate of a German receipt stays
  // German inside a batch whose default is English. Falls back to the
  // global default, then the interface language (legacy sessions restored
  // from IDB predate the per-session field).
  const sessionOcrLang = (id: string): string =>
    sessions.find((s) => s.id === id)?.lang ?? ocrLang ?? lang;

  // Answer from the first-upload language prompt: remember the choice as
  // the default for every later receipt, then run the files it gated.
  const handleReceiptLangConfirm = (chosen: UiLangCode): void => {
    setOcrLang(chosen);
    const files = pendingFiles;
    setPendingFiles(null);
    if (files === null || files.length === 0) return;
    processFiles(files, chosen);
    trackEvent('file_uploaded', { count: files.length });
  };

  // Set / clear the category on a session. When the user actively picks a
  // category (not when clearing), remember the vendor→category mapping so
  // future receipts from the same vendor pre-fill the dropdown via the
  // store's setResult auto-suggest.
  const handleSetCategory = (
    id: string,
    category: CategoryCode | undefined,
  ): void => {
    const session = sessions.find((s) => s.id === id);
    if (session === undefined || session.receipt === null) return;
    const next = { ...session.receipt };
    if (category === undefined) delete next.category;
    else next.category = category;
    // User picked or cleared the category — by definition authoritative,
    // so drop the auto-detection score (the "auto · NN%" badge disappears).
    delete next.categoryScore;
    updateReceipt(id, next);
    if (category !== undefined) {
      rememberVendorCategory(session.receipt.vendor.value, category);
    }
  };

  // VAT percentage for one receipt. Empty input clears it back to
  // "derive from the parsed tax" rather than storing 0 — 0 is a real,
  // distinct answer that exports as `Exempt`.
  const handleSetTaxRate = (id: string, rate: number | undefined): void => {
    const session = sessions.find((s) => s.id === id);
    if (session === undefined || session.receipt === null) return;
    const next = { ...session.receipt };
    if (rate === undefined) delete next.taxRate;
    else next.taxRate = rate;
    updateReceipt(id, next);
  };

  // Copy one receipt's VAT rate onto every other parsed receipt. Batches
  // are typically one country at one rate, so typing it once and
  // stamping the batch beats editing every card.
  const handleApplyTaxRateToAll = (rate: number): void => {
    for (const session of sessions) {
      if (session.state.kind !== 'success' || session.receipt === null) continue;
      if (session.receipt.taxRate === rate) continue;
      updateReceipt(session.id, { ...session.receipt, taxRate: rate });
    }
  };

  // Overlay-dialog priority guard: at most ONE of the fullscreen dialogs
  // renders at a time, so their window-level Escape handlers can't
  // collide (one Escape firing a download AND committing an OCR
  // language). Lower-priority dialogs keep their state pending and
  // appear as soon as the higher one closes.
  // Priority: ReceiptLanguageDialog > CropModal > DonationDialog.
  const showLanguageDialog = pendingFiles !== null;
  const showCropModal =
    !showLanguageDialog &&
    croppingSession !== null &&
    croppingSession.originalFile !== null;
  const showDonationDialog =
    !showLanguageDialog && !showCropModal && pendingExport !== null;

  // Click handler: queues the format, opens the donation modal. The
  // actual download fires from `runExport` below when the modal's
  // countdown ends.
  const handleExport = (format: ExportFormat): void => {
    if (doneCount === 0) return;
    setPendingExport(format);
    trackEvent('export_requested', { format });
  };

  // Fired by DonationDialog when its 5-second countdown ends OR the
  // user clicks "Skip". Runs the actual download; clears the pending
  // state so the modal unmounts.
  const runExport = async (format: ExportFormat): Promise<void> => {
    setPendingExport(null);
    if (doneCount === 0) return;
    const excludeTax = excludeTaxFromExports;
    if (format === 'csv') {
      triggerDownload(
        exportCsvBatch(doneSessions, { excludeTax }),
        batchFilename('csv'),
      );
      trackEvent('export_completed', { format, count: doneCount });
      return;
    }
    if (format === 'billcsv') {
      // No excludeTax handling: the template has no Tax column to omit,
      // and *LineTaxCode is mandatory so it can't be blanked out.
      triggerDownload(
        exportBillCsvBatch(doneSessions),
        batchFilename('csv'),
      );
      trackEvent('export_completed', { format, count: doneCount });
      return;
    }
    if (format === 'xlsx') {
      triggerDownload(
        await exportXlsxBatch(doneSessions, { excludeTax }),
        batchFilename('xlsx'),
      );
      trackEvent('export_completed', { format, count: doneCount });
      return;
    }
    // ZIP: skipped silently if every session is missing its corrected-image
    // blob (shouldn't happen post-success, but the export is defensive).
    const zipBlob = await exportZipBatch(doneSessions, { excludeTax });
    if (zipBlob !== null) {
      triggerDownload(zipBlob, batchFilename('zip'));
      trackEvent('export_completed', { format, count: doneCount });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-8">
        <header className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-lg shadow-indigo-500/30 sm:h-10 sm:w-10 dark:shadow-indigo-700/40">
              <BrandMark className="h-7 w-7 sm:h-8 sm:w-8" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-lg font-bold tracking-tight text-slate-900 sm:text-xl dark:text-slate-50">
                receipt-ripper.com
              </p>
              <p className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                <Lock className="h-3 w-3" aria-hidden="true" />
                <span>{t(lang, 'tagline.onDevice')}</span>
              </p>
            </div>
          </div>
          {/* Header nav. Hidden on the smallest screens because the brand
              tagline + theme toggle already use the full row width;
              the same destinations live in the footer for mobile users. */}
          <nav
            aria-label="Site navigation"
            className="hidden items-center gap-1 sm:flex"
          >
            <a
              href={lang === 'eng' ? '/' : `/${HTML_LANG[lang]}/`}
              aria-current="page"
              className="rounded-md px-2.5 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 ring-1 ring-inset ring-indigo-600/20 transition-colors hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/30 dark:hover:bg-indigo-500/15"
            >
              {t(lang, 'nav.home')}
            </a>
            <a
              href={lang === 'eng' ? '/guides/' : `/${HTML_LANG[lang]}/guides/`}
              className="rounded-md px-2.5 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-indigo-50 hover:text-indigo-700 dark:text-slate-300 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-300"
            >
              {t(lang, 'footer.guides')}
            </a>
            <a
              href={staticPagePath(lang, 'faq')}
              className="rounded-md px-2.5 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-indigo-50 hover:text-indigo-700 dark:text-slate-300 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-300"
            >
              {t(lang, 'footer.faq')}
            </a>
            <a
              href={staticPagePath(lang, 'contact')}
              className="rounded-md px-2.5 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-indigo-50 hover:text-indigo-700 dark:text-slate-300 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-300"
            >
              {t(lang, 'footer.contact')}
            </a>
          </nav>
          <div className="flex flex-shrink-0 items-center gap-2">
            {DEBUG && showDebug && (
              <DebugFolderControl onChange={setDebugFolder} />
            )}
            <ThemeToggle />
          </div>
        </header>

        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-wrap items-center gap-3">
            <label
              htmlFor="lang-select"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              <Languages className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only sm:not-sr-only">{t(lang, 'language.label')}</span>
            </label>
            <select
              id="lang-select"
              value={lang}
              onChange={(e) => setLang(e.target.value as UiLangCode)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-400"
            >
              {LANG_OPTIONS.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.label}
                </option>
              ))}
            </select>
            <label
              htmlFor="ocr-lang-select"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              <ReceiptText className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only sm:not-sr-only">{t(lang, 'language.receiptLabel')}</span>
            </label>
            <select
              id="ocr-lang-select"
              value={ocrLang ?? lang}
              onChange={(e) => setOcrLang(e.target.value as UiLangCode)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-400"
            >
              {LANG_OPTIONS.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="order-last basis-full text-xs text-slate-400 sm:order-none sm:basis-auto dark:text-slate-500">
              {t(lang, 'language.firstUseDownload')}
            </span>
            <button
              type="button"
              onClick={() => setPersistEnabled(!persistEnabled)}
              aria-label={t(lang, 'persistence.label')}
              aria-pressed={persistEnabled}
              title={t(lang, 'persistence.tooltip')}
              className={`ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
                persistEnabled
                  ? 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-600/20 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/30'
                  : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300'
              }`}
            >
              <Database className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setExcludeTaxFromExports(!excludeTaxFromExports)}
              aria-label={t(lang, 'excludeTax.label')}
              aria-pressed={excludeTaxFromExports}
              title={t(lang, 'excludeTax.tooltip')}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
                excludeTaxFromExports
                  ? 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-600/20 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/30'
                  : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300'
              }`}
            >
              <Percent className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            {DEBUG && (
              <button
                type="button"
                onClick={() => setShowDebug(!showDebug)}
                aria-label={t(lang, 'debug.show')}
                aria-pressed={showDebug}
                title={t(lang, 'debug.show')}
                className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
                  showDebug
                    ? 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-600/20 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/30'
                    : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300'
                }`}
              >
                <Bug className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
        </section>

        <MarketingHero lang={lang} />

        <AdBanner />

        <Dropzone onFiles={(files) => void handleFiles(files)} />

        <BatchProgress sessions={sessions} />

        <BatchSummary sessions={sessions} lang={lang} />

        <SessionsList
          sessions={sessions}
          lang={lang}
          ocrLang={ocrLang}
          onEditReceipt={updateReceipt}
          onRemoveSession={removeSession}
          onRemoveDone={removeTerminal}
          onClearAll={cancel}
          onTrySample={async () => {
            const file = await buildSampleReceiptFile();
            if (file !== null) void handleFiles([file]);
          }}
          onManualCrop={(id) => setCroppingId(id)}
          onConfirmScan={(id) => confirmScan(id, sessionOcrLang(id))}
          onSetCategory={handleSetCategory}
          onSetTaxRate={handleSetTaxRate}
          onApplyTaxRateToAll={handleApplyTaxRateToAll}
          onRotate={(id) => void reprocessWithRotation(id, sessionOcrLang(id))}
          onChangeLang={(id, next) => reprocessWithLang(id, next)}
          onReorder={reorderSessions}
        />

        {sessions.length > 0 && (
          <ScanAnother
            lang={lang}
            onFiles={(files) => void handleFiles(files)}
          />
        )}

        <ExportMenu count={doneCount} onExport={handleExport} />

        {/* The three `showX` flags below are mutually exclusive — see the
            priority guard above. The extra null checks re-establish TS
            narrowing that the boolean flags can't carry. */}
        {showCropModal && croppingSession !== null && croppingSession.originalFile !== null && (
          <CropModal
            file={croppingSession.originalFile}
            filename={croppingSession.filename}
            {...(croppingSession.detectedQuad !== undefined
              ? { initialQuad: croppingSession.detectedQuad }
              : {})}
            onClose={() => setCroppingId(null)}
            onApply={(quad) => {
              reprocessWithManualQuad(croppingSession.id, quad, sessionOcrLang(croppingSession.id));
              setCroppingId(null);
            }}
          />
        )}

        {showLanguageDialog && (
          <ReceiptLanguageDialog lang={lang} onConfirm={handleReceiptLangConfirm} />
        )}

        {showDonationDialog && pendingExport !== null && (
          <DonationDialog
            lang={lang}
            onProceed={() => void runExport(pendingExport)}
            onClose={() => setPendingExport(null)}
          />
        )}

        <MarketingContent lang={lang} />

        <footer className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 pt-4 text-center text-xs text-slate-400 dark:text-slate-500">
          <span>{t(lang, 'footer.dataPrivacy')}</span>
          <span aria-hidden="true">·</span>
          <a
            href={staticPagePath(lang, 'about')}
            className="underline-offset-4 hover:text-slate-600 hover:underline dark:hover:text-slate-300"
          >
            {t(lang, 'footer.about')}
          </a>
          <span aria-hidden="true">·</span>
          <a
            href={staticPagePath(lang, 'faq')}
            className="underline-offset-4 hover:text-slate-600 hover:underline dark:hover:text-slate-300"
          >
            {t(lang, 'footer.faq')}
          </a>
          <span aria-hidden="true">·</span>
          <a
            href={lang === 'eng' ? '/guides/' : `/${HTML_LANG[lang]}/guides/`}
            className="underline-offset-4 hover:text-slate-600 hover:underline dark:hover:text-slate-300"
          >
            {t(lang, 'footer.guides')}
          </a>
          <span aria-hidden="true">·</span>
          <a
            href={staticPagePath(lang, 'contact')}
            className="underline-offset-4 hover:text-slate-600 hover:underline dark:hover:text-slate-300"
          >
            {t(lang, 'footer.contact')}
          </a>
          <span aria-hidden="true">·</span>
          <a
            href={staticPagePath(lang, 'privacy')}
            className="underline-offset-4 hover:text-slate-600 hover:underline dark:hover:text-slate-300"
          >
            {t(lang, 'footer.privacy')}
          </a>
          <span aria-hidden="true">·</span>
          <a
            href={staticPagePath(lang, 'terms')}
            className="underline-offset-4 hover:text-slate-600 hover:underline dark:hover:text-slate-300"
          >
            {t(lang, 'footer.terms')}
          </a>
          <span aria-hidden="true">·</span>
          <button
            type="button"
            onClick={() => {
              clearConsent();
              // Reload so any already-injected gtag / adsbygoogle
              // scripts don't keep tracking after a switch from
              // accepted → rejected. The fresh page either re-shows
              // the banner (consent cleared) and lets the user choose
              // again, or stays clean if they tab away.
              window.location.reload();
            }}
            className="underline-offset-4 hover:text-slate-600 hover:underline dark:hover:text-slate-300"
          >
            {t(lang, 'consent.preferences')}
          </button>
          <span aria-hidden="true">·</span>
          <a
            href="/LICENSES.txt"
            target="_blank"
            rel="noopener"
            className="underline-offset-4 hover:text-slate-600 hover:underline dark:hover:text-slate-300"
          >
            {t(lang, 'footer.licenses')}
          </a>
        </footer>
      </div>
      {showConsent && (
        <CookieBanner lang={lang} onDismiss={() => setShowConsent(false)} />
      )}
    </div>
  );
}
